import { ACTIVE_URL } from "@/constants";
import { apiFetch } from "@/hooks/apiFetch";

const API_BASE = `${ACTIVE_URL}/api`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ContactMessage {
  id: number;
  name: string;
  email: string;
  phone: string;
  company: string;
  subject: string;
  message: string;
  source: string;
  created_at: string;
}

export interface Partner {
  id: number;
  name: string;
  logo_url: string;
  website_url?: string;
  order: number;
}

export interface NovaDeal {
  id: number;
  name: string;
  image_url: string;
  order: number;
}

export interface NovaPerk {
  id: number;
  stat: string;
  label: string;
  description: string;
  order: number;
}

export interface NewsItem {
  id: number;
  title: string;
  description: string;
  link: string;
  image_url: string;
  created_at: string;
}

export interface QrRedirect {
  id: number;
  path: string;
  redirect_from: string;
  active: boolean;
  updated_at: string;
}

export interface RedirectClick {
  id: number;
  date: string;
  app: string;
  user_agent: string;
  redirect_from: string;
  campaign_id: string;
}

export interface PageContent {
  title: string;
  content: string;
}

export type EmailSettingsKind = "messages" | "leads";

export interface EmailSettings {
  email_host: string;
  email_port: number;
  email_host_user: string;
  email_host_password: string;
  email_use_tls: boolean;
  default_from_email: string;
  contact_notify_email: string;
}

// ---------------------------------------------------------------------------
// Landing pages
// ---------------------------------------------------------------------------

export type FieldType =
  | "first_name"
  | "last_name"
  | "email"
  | "phone"
  | "multiple_select"
  | "multiple_choice"
  | "checkbox";

export type SectionType = "text" | "image" | "questionnaire" | "hero" | "steps" | "cards";

export interface QuestionnaireField {
  id?: number;
  field_type: FieldType;
  label: string;
  required: boolean;
  options: string[];
  order: number;
}

export interface LandingPageStep {
  id?: number;
  text: string;
  order: number;
  icon_url?: string | null;
}

export interface LandingPageCard {
  id?: number;
  title: string;
  description: string;
  link_text?: string;
  link_url?: string;
  order: number;
  icon_url?: string | null;
}

export interface LandingPageSection {
  id?: number;
  section_type: SectionType;
  order: number;
  heading: string;
  body: string;
  image_url?: string | null;
  button_text?: string;
  button_url?: string;
  integration_code?: string;
  questionnaire_fields: QuestionnaireField[];
  steps: LandingPageStep[];
  cards: LandingPageCard[];
}

export interface LandingPageSummary {
  id: number;
  slug: string;
  title: string;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

export interface LandingPageDetail extends LandingPageSummary {
  sections: LandingPageSection[];
}

export interface QuestionnaireAnswer {
  id: number;
  field: number;
  field_label: string;
  field_type: FieldType;
  value: string;
}

export interface LandingPageSubmission {
  id: number;
  section: number;
  created_at: string;
  answers: QuestionnaireAnswer[];
}

/** A submission from the all-pages endpoint, tagged with its landing page. */
export interface QuestionnaireSubmission extends LandingPageSubmission {
  page_slug: string;
  page_title: string;
}

/** Django REST Framework's page-number pagination envelope. */
export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ---------------------------------------------------------------------------
// Section integrations (e.g. Unistudents) — public, unauthenticated calls,
// same pattern as submitLandingPageQuestionnaire below: raw fetch, not
// apiFetch, since visitors aren't logged in.
// ---------------------------------------------------------------------------

export interface IntegrationResult {
  status:
    | "success"
    | "already_claimed"
    | "otp_required"
    | "otp_max_attempts"
    | "invalid_otp"
    | "error";
  points?: string;
  detail?: string;
}

export class IntegrationRequestError extends Error {
  detail: string;
  constructor(detail: string) {
    super(detail);
    this.detail = detail;
  }
}

async function integrationRequest(
  path: string,
  body: Record<string, unknown>,
): Promise<IntegrationResult> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Field-validation failures come back shaped like the generic
    // questionnaire submit endpoint ({errors: {...}}) — reuse the same
    // error class so the UI's existing fieldErrors handling just works.
    if (data.errors) {
      throw new LandingPageSubmitError(data.errors);
    }
    throw new IntegrationRequestError(data.detail || "Κάτι πήγε στραβά.");
  }
  return data as IntegrationResult;
}

export function startSectionIntegration(
  slug: string,
  sectionId: number,
  data: { answers: Record<string, string | string[] | boolean>; country: string },
): Promise<IntegrationResult> {
  return integrationRequest(
    `/landing-pages/${slug}/sections/${sectionId}/integration/start/`,
    data,
  );
}

export function verifySectionIntegration(
  slug: string,
  sectionId: number,
  data: { mobile: string; country: string; otp: string },
): Promise<IntegrationResult> {
  return integrationRequest(
    `/landing-pages/${slug}/sections/${sectionId}/integration/verify/`,
    data,
  );
}

// ---------------------------------------------------------------------------
// Helper — parse JSON, throwing on a non-OK response.
// ---------------------------------------------------------------------------

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Messages / leads
// ---------------------------------------------------------------------------

export function getMessages(source?: string): Promise<ContactMessage[]> {
  const url = source
    ? `${API_BASE}/messages/?source=${source}`
    : `${API_BASE}/messages/`;
  return apiFetch(url).then(json<ContactMessage[]>);
}

// ---------------------------------------------------------------------------
// Partners
// ---------------------------------------------------------------------------

export function getPartners(): Promise<Partner[]> {
  return apiFetch(`${API_BASE}/partners/`).then(json<Partner[]>);
}

export function createPartner(data: {
  name: string;
  logo: File;
  website_url: string;
  order: number;
}): Promise<void> {
  const fd = new FormData();
  fd.append("name", data.name);
  fd.append("logo", data.logo);
  fd.append("website_url", data.website_url);
  fd.append("order", String(data.order));
  return apiFetch(`${API_BASE}/partners/create/`, {
    method: "POST",
    body: fd,
  }).then(() => undefined);
}

export function reorderPartners(ids: number[]): Promise<Partner[]> {
  return apiFetch(`${API_BASE}/partners/reorder/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  }).then(json<Partner[]>);
}

export function deletePartner(id: number): Promise<void> {
  return apiFetch(`${API_BASE}/partners/${id}/delete/`, {
    method: "DELETE",
  }).then(() => undefined);
}

// ---------------------------------------------------------------------------
// QR redirects  (list is public; update requires auth)
// ---------------------------------------------------------------------------

export function getQrRedirects(): Promise<QrRedirect[]> {
  return apiFetch(`${API_BASE}/qr-redirects/`).then(json<QrRedirect[]>);
}

export function getRedirectClicks(): Promise<RedirectClick[]> {
  return apiFetch(`${API_BASE}/redirect-clicks/`).then(json<RedirectClick[]>);
}

/** Creates missing rows for registry paths; never touches existing rows. */
export function syncQrRedirects(
  items: { path: string; redirect_from: string }[],
): Promise<QrRedirect[]> {
  return apiFetch(`${API_BASE}/qr-redirects/sync/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(items),
  }).then(json<QrRedirect[]>);
}

export function updateQrRedirect(
  id: number,
  data: { active: boolean },
): Promise<QrRedirect> {
  return apiFetch(`${API_BASE}/qr-redirects/${id}/update/`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }).then(json<QrRedirect>);
}

// ---------------------------------------------------------------------------
// Page videos  (GET is public; upload/delete require auth)
// ---------------------------------------------------------------------------

export function getPageVideo(slug: string): Promise<string | null> {
  return fetch(`${API_BASE}/page-video/${slug}/`)
    .then((r) => r.json())
    .then((data) => data?.video_url ?? null);
}

export function uploadPageVideo(slug: string, video: File): Promise<void> {
  const fd = new FormData();
  fd.append("video", video);
  return apiFetch(`${API_BASE}/page-video/${slug}/upload/`, {
    method: "POST",
    body: fd,
  }).then(() => undefined);
}

export function deletePageVideo(slug: string): Promise<void> {
  return apiFetch(`${API_BASE}/page-video/${slug}/delete/`, {
    method: "DELETE",
  }).then(() => undefined);
}

// ---------------------------------------------------------------------------
// Page images  (GET is public; upload/delete require auth)
// ---------------------------------------------------------------------------

export function getPageImage(slug: string): Promise<string | null> {
  return fetch(`${API_BASE}/page-image/${slug}/`)
    .then((r) => r.json())
    .then((data) => data?.image_url ?? null);
}

export function uploadPageImage(slug: string, image: File): Promise<void> {
  const fd = new FormData();
  fd.append("image", image);
  return apiFetch(`${API_BASE}/page-image/${slug}/upload/`, {
    method: "POST",
    body: fd,
  }).then(() => undefined);
}

export function deletePageImage(slug: string): Promise<void> {
  return apiFetch(`${API_BASE}/page-image/${slug}/delete/`, {
    method: "DELETE",
  }).then(() => undefined);
}

// ---------------------------------------------------------------------------
// News
// ---------------------------------------------------------------------------

export function getNews(): Promise<NewsItem[]> {
  return apiFetch(`${API_BASE}/news/`).then(json<NewsItem[]>);
}

export function createNews(data: {
  title: string;
  description: string;
  link: string;
  image?: File | null;
}): Promise<void> {
  const fd = new FormData();
  fd.append("title", data.title);
  fd.append("description", data.description);
  fd.append("link", data.link);
  if (data.image) fd.append("image", data.image);
  return apiFetch(`${API_BASE}/news/create/`, {
    method: "POST",
    body: fd,
  }).then(() => undefined);
}

export function updateNews(
  id: number,
  data: {
    title: string;
    description: string;
    image?: File | null;
    removeImage?: boolean;
  },
): Promise<void> {
  const fd = new FormData();
  fd.append("title", data.title);
  fd.append("description", data.description);
  if (data.image) fd.append("image", data.image);
  else if (data.removeImage) fd.append("remove_image", "true");
  return apiFetch(`${API_BASE}/news/${id}/update/`, {
    method: "PATCH",
    body: fd,
  }).then(() => undefined);
}

export function deleteNews(id: number): Promise<void> {
  return apiFetch(`${API_BASE}/news/${id}/delete/`, {
    method: "DELETE",
  }).then(() => undefined);
}

// ---------------------------------------------------------------------------
// Nova Deals
// ---------------------------------------------------------------------------

export function getNovaDeal(): Promise<NovaDeal[]> {
  return apiFetch(`${API_BASE}/nova-deals/`).then(json<NovaDeal[]>);
}

export function createNovaDeal(data: {
  name: string;
  image: File;
  order: number;
}): Promise<NovaDeal> {
  const fd = new FormData();
  fd.append("name", data.name);
  fd.append("image", data.image);
  fd.append("order", String(data.order));
  return apiFetch(`${API_BASE}/nova-deals/create/`, {
    method: "POST",
    body: fd,
  }).then(json<NovaDeal>);
}

export function deleteNovaDeal(id: number): Promise<void> {
  return apiFetch(`${API_BASE}/nova-deals/${id}/delete/`, {
    method: "DELETE",
  }).then(() => undefined);
}

// ---------------------------------------------------------------------------
// Nova perks  (GET is public; update requires auth)
// ---------------------------------------------------------------------------

export function getNovaPerks(): Promise<NovaPerk[]> {
  return fetch(`${API_BASE}/nova-perks/`).then(json<NovaPerk[]>);
}

export function updateNovaPerk(
  id: number,
  data: { stat: string; label: string; description: string },
): Promise<NovaPerk> {
  return apiFetch(`${API_BASE}/nova-perks/${id}/update/`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }).then(json<NovaPerk>);
}

// ---------------------------------------------------------------------------
// Page content
// ---------------------------------------------------------------------------

export function getPageContent(slug: string): Promise<PageContent> {
  return fetch(`${API_BASE}/content/${slug}/`)
    .then((r) => r.json())
    .then((data) => ({
      title: data.title ?? "",
      content: data.content ?? "",
    }));
}

export function updatePageContent(
  slug: string,
  data: PageContent,
): Promise<void> {
  return apiFetch(`${API_BASE}/content/${slug}/update/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }).then((res) => {
    if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  });
}

// ---------------------------------------------------------------------------
// Email settings
// ---------------------------------------------------------------------------

export function getEmailSettings(kind: EmailSettingsKind): Promise<EmailSettings> {
  return apiFetch(`${API_BASE}/email-settings/${kind}/`).then(json<EmailSettings>);
}

export function updateEmailSettings(
  kind: EmailSettingsKind,
  data: EmailSettings,
): Promise<EmailSettings> {
  return apiFetch(`${API_BASE}/email-settings/${kind}/update/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }).then(json<EmailSettings>);
}

// ---------------------------------------------------------------------------
// Landing pages  (list/detail/update/delete require auth; public GET is open)
// ---------------------------------------------------------------------------

export function listLandingPages(): Promise<LandingPageSummary[]> {
  return apiFetch(`${API_BASE}/landing-pages/`).then(json<LandingPageSummary[]>);
}

export function createLandingPage(
  slug: string,
  title: string,
): Promise<LandingPageDetail> {
  return apiFetch(`${API_BASE}/landing-pages/create/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slug, title }),
  }).then(json<LandingPageDetail>);
}

export function getLandingPage(slug: string): Promise<LandingPageDetail> {
  return apiFetch(`${API_BASE}/landing-pages/${slug}/`).then(json<LandingPageDetail>);
}

export function getPublicLandingPage(slug: string): Promise<LandingPageDetail> {
  return fetch(`${API_BASE}/landing-pages/${slug}/public/`).then(json<LandingPageDetail>);
}

export function updateLandingPage(
  slug: string,
  data: { title: string; is_published: boolean; sections: LandingPageSection[]; slug?: string },
): Promise<LandingPageDetail> {
  return apiFetch(`${API_BASE}/landing-pages/${slug}/update/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }).then(json<LandingPageDetail>);
}

export function deleteLandingPage(slug: string): Promise<void> {
  return apiFetch(`${API_BASE}/landing-pages/${slug}/delete/`, {
    method: "DELETE",
  }).then(() => undefined);
}

export function uploadLandingPageSectionImage(
  slug: string,
  sectionId: number,
  image: File,
): Promise<{ image_url: string | null }> {
  const fd = new FormData();
  fd.append("image", image);
  return apiFetch(`${API_BASE}/landing-pages/${slug}/sections/${sectionId}/image/`, {
    method: "POST",
    body: fd,
  }).then(json<{ image_url: string | null }>);
}

export function uploadLandingPageStepIcon(
  slug: string,
  stepId: number,
  icon: File,
): Promise<{ icon_url: string | null }> {
  const fd = new FormData();
  fd.append("icon", icon);
  return apiFetch(`${API_BASE}/landing-pages/${slug}/steps/${stepId}/icon/`, {
    method: "POST",
    body: fd,
  }).then(json<{ icon_url: string | null }>);
}

export function uploadLandingPageCardIcon(
  slug: string,
  cardId: number,
  icon: File,
): Promise<{ icon_url: string | null }> {
  const fd = new FormData();
  fd.append("icon", icon);
  return apiFetch(`${API_BASE}/landing-pages/${slug}/cards/${cardId}/icon/`, {
    method: "POST",
    body: fd,
  }).then(json<{ icon_url: string | null }>);
}

export function getLandingPageSubmissions(
  slug: string,
): Promise<LandingPageSubmission[]> {
  return apiFetch(`${API_BASE}/landing-pages/${slug}/submissions/`).then(
    json<LandingPageSubmission[]>,
  );
}

// ---------------------------------------------------------------------------
// Questionnaire submissions across all landing pages (requires auth)
// ---------------------------------------------------------------------------

/** One page of submissions, newest first, optionally for a single landing page. */
export function listQuestionnaireSubmissions(params: {
  page: number;
  pageSize: number;
  landingPage?: string;
}): Promise<Paginated<QuestionnaireSubmission>> {
  const qs = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.pageSize),
  });
  if (params.landingPage) qs.set("landing_page", params.landingPage);
  return apiFetch(`${API_BASE}/landing-page-submissions/?${qs}`).then(
    json<Paginated<QuestionnaireSubmission>>,
  );
}

/** Every matching submission in one unpaginated list (used by the Excel export). */
export function listAllQuestionnaireSubmissions(
  params: { landingPage?: string } = {},
): Promise<QuestionnaireSubmission[]> {
  const qs = new URLSearchParams({ all: "true" });
  if (params.landingPage) qs.set("landing_page", params.landingPage);
  return apiFetch(`${API_BASE}/landing-page-submissions/?${qs}`).then(
    json<QuestionnaireSubmission[]>,
  );
}

export class LandingPageSubmitError extends Error {
  errors: Record<string, string>;
  constructor(errors: Record<string, string>) {
    super("Validation failed");
    this.errors = errors;
  }
}

export async function submitLandingPageQuestionnaire(
  slug: string,
  sectionId: number,
  answers: Record<string, string | string[] | boolean>,
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/landing-pages/${slug}/sections/${sectionId}/submit/`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ answers }),
    }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new LandingPageSubmitError(data.errors ?? {});
  }
}
